import { Box, Center, Table, Tbody, Td, Text, Th, Thead, Tr, VStack } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    fetchFacilitiesInactiveLastMonth,
    fetchLowestFacilitiesWithLastPickup,
    fetchPickupsStatsByCounty
} from '../../api'; // Adjust path
import Loading from '../../components/Loading/Loading';
import ActiveFacilitiesLastMonth from './FacilityLastMonth';

const PickupsStatsByCountyPage = () => {
    const [data, setData] = useState({});
    const [inactiveData, setInactiveData] = useState({});
    const [loading, setLoading] = useState(true);
    const [loadingInactive, setLoadingInactive] = useState(true);

    // New state for lowest facilities with last pickup
    const [lowestFacilitiesCity, setLowestFacilitiesCity] = useState([]);
    const [lowestFacilitiesCounty, setLowestFacilitiesCounty] = useState([]);
    const [loadingLowest, setLoadingLowest] = useState(true);

    const { labId } = useParams();

    useEffect(() => {
        const loadStats = async () => {
            setLoading(true);
            try {
                const res = await fetchPickupsStatsByCounty(labId);
                if (res.success) setData(res.data);
            } catch (err) {
                console.error('Error loading stats:', err);
            }
            setLoading(false);
        };

        const loadInactive = async () => {
            setLoadingInactive(true);
            try {
                const res = await fetchFacilitiesInactiveLastMonth(labId);
                if (res.success) setInactiveData(res.data);
            } catch (err) {
                console.error('Error loading inactive:', err);
            }
            setLoadingInactive(false);
        };

        // New: Load lowest facilities with last pickup
        const loadLowestFacilities = async () => {
            setLoadingLowest(true);
            try {
                const res = await fetchLowestFacilitiesWithLastPickup(labId);
                if (res.success && res.data) {
                    setLowestFacilitiesCity(res.data.lowestFacilitiesCity.slice(0, 10));
                    setLowestFacilitiesCounty(res.data.lowestFacilitiesCounty.slice(0, 10));
                }
            } catch (err) {
                console.error('Error loading lowest facilities:', err);
            }
            setLoadingLowest(false);
        };

        loadStats();
        loadInactive();
        loadLowestFacilities();
    }, [labId]);

    // Existing stats prep...
    let countiesArr = [];
    let citiesArr = [];
    if (data && Object.keys(data).length) {
        countiesArr = Object.entries(data)
            .filter(([county]) => county !== 'Unknown County')
            .map(([county, info]) => ({
                county,
                pickupCount: info.pickupCount,
                facilityCount: Object.values(info.cities)
                    .filter(city => city !== 'unknown')
                    .reduce((sum, city) => sum + (city.facilities?.length || 0), 0),
                totalSamples: info.totalSamples,
            }));

        Object.entries(data).forEach(([county, info]) => {
            if (county === 'Unknown County') return;
            Object.entries(info.cities).forEach(([city, cinfo]) => {
                if (city === 'unknown') return;
                citiesArr.push({
                    county,
                    city,
                    pickupCount: cinfo.pickupCount,
                    facilityCount: cinfo.facilities?.length || 0,
                    totalSamples: cinfo.totalSamples,
                });
            });
        });
    }

    // Sorting helpers (lowest 10, skip 0s)
    const lowestSamplesCity = citiesArr.filter(c => c.totalSamples > 0).sort((a, b) => a.totalSamples - b.totalSamples).slice(0, 10);
    const lowestSamplesCounty = countiesArr.filter(c => c.totalSamples > 0).sort((a, b) => a.totalSamples - b.totalSamples).slice(0, 10);
    const lowestPickupsCity = citiesArr.filter(c => c.pickupCount > 0).sort((a, b) => a.pickupCount - b.pickupCount).slice(0, 10);
    const lowestPickupsCounty = countiesArr.filter(c => c.pickupCount > 0).sort((a, b) => a.pickupCount - b.pickupCount).slice(0, 10);

    // Helper to render tables (scrollable if requested)
    const renderTable = (rows, cols, title, scrollable = false) => (
        <Box border="1px solid #ddd" borderRadius="md" p={3} mb={6} w="100%" maxH={scrollable ? "350px" : "none"} overflowY={scrollable ? "auto" : "visible"}>
            <Text fontWeight="bold" fontSize="md" mb={2}>{title}</Text>
            <Table variant="simple" size="sm">
                <Thead>
                    <Tr>
                        <Th>#</Th>
                        {cols.map((col, i) => <Th key={i}>{col}</Th>)}
                    </Tr>
                </Thead>
                <Tbody>
                    {rows.map((row, idx) => (
                        <Tr key={idx}>
                            <Td>{idx + 1}</Td>
                            {cols.map((col, i) => <Td key={i}>{row[col]}</Td>)}
                        </Tr>
                    ))}
                </Tbody>
            </Table>
        </Box>
    );

    // Helper for inactive facility tables (now scrollable)
    const renderInactiveFacilities = (type, title) => {
        if (loadingInactive) {
            return <Center><Loading /></Center>;
        }
        let rows = [];
        Object.entries(inactiveData)
            .filter(([county]) => county !== 'Unknown County')
            .forEach(([county, cities]) => {
                Object.entries(cities)
                    .filter(([city]) => city !== 'unknown')
                    .forEach(([city, group]) => {
                        if (group[type]?.length > 0) {
                            group[type].forEach(facility => {
                                rows.push({
                                    county,
                                    city: city.charAt(0).toUpperCase() + city.slice(1),
                                    name: facility.name
                                });
                            });
                        }
                    });
            });
        return renderTable(rows, ['county', 'city', 'name'], title, true);
    };

    if (loading || loadingLowest) {
        return (
            <Center mt="20%">
                <Loading />
            </Center>
        );
    }

    return (
        <Box maxW="6xl" mx="auto" p="8">
            <VStack align="start" spacing={8}>
                <Text fontSize="2xl" fontWeight="bold">Pickups & Samples by County</Text>



                <ActiveFacilitiesLastMonth labId={labId} />





                {/* New: Inactive facilities (now scrollable) */}
                {renderInactiveFacilities('facilitiesNoPickup', 'Facilities With NO Pickups In The Last Month')}
                {renderInactiveFacilities('facilitiesNoSamples', 'Facilities With NO Samples Sent In The Last Month')}

                {/* Lowest facilities with last pickup (NEW) */}
                <Box w="100%">
                    {renderTable(
                        lowestFacilitiesCity.map(r => ({
                            city: r.city.charAt(0).toUpperCase() + r.city.slice(1),
                            county: r.county,
                            facilityCount: r.facilityCount,
                            lastPickup: r.lastPickup ? new Date(r.lastPickup).toLocaleDateString() : 'Never',
                        })),
                        ['city', 'county', 'facilityCount', 'lastPickup'],
                        'Lowest Facilities per City (with Last Pickup)'
                    )}

                    {renderTable(
                        lowestFacilitiesCounty.map(r => ({
                            county: r.county,
                            facilityCount: r.facilityCount,
                            lastPickup: r.lastPickup ? new Date(r.lastPickup).toLocaleDateString() : 'Never',
                        })),
                        ['county', 'facilityCount', 'lastPickup'],
                        'Lowest Facilities per County (with Last Pickup)'
                    )}

                    {/* Your existing tables */}
                    {renderTable(
                        lowestSamplesCity.map(r => ({
                            city: r.city.charAt(0).toUpperCase() + r.city.slice(1),
                            county: r.county,
                            totalSamples: r.totalSamples
                        })),
                        ['city', 'county', 'totalSamples'],
                        'Lowest Samples Count per City'
                    )}

                    {renderTable(
                        lowestSamplesCounty.map(r => ({
                            county: r.county,
                            totalSamples: r.totalSamples
                        })),
                        ['county', 'totalSamples'],
                        'Lowest Samples Count per County'
                    )}

                    {renderTable(
                        lowestPickupsCity.map(r => ({
                            city: r.city.charAt(0).toUpperCase() + r.city.slice(1),
                            county: r.county,
                            pickupCount: r.pickupCount
                        })),
                        ['city', 'county', 'pickupCount'],
                        'Lowest Pickups Count per City'
                    )}

                    {renderTable(
                        lowestPickupsCounty.map(r => ({
                            county: r.county,
                            pickupCount: r.pickupCount
                        })),
                        ['county', 'pickupCount'],
                        'Lowest Pickups Count per County'
                    )}
                </Box>

                {/* The main full county/city stats (your original big tables) */}
                {Object.entries(data)
                    .filter(([county]) => county !== 'Unknown County')
                    .map(([county, info]) => (
                        <Box key={county} w="100%" mt={4}>
                            <Text fontWeight="bold" fontSize="xl" mb={2}>{county}</Text>
                            <Box border="1px solid #ddd" borderRadius="md" maxH="350px" overflowY="auto">
                                <Table variant="simple" size="sm">
                                    <Thead>
                                        <Tr>
                                            <Th>#</Th>
                                            <Th>City</Th>
                                            <Th isNumeric>Pickups</Th>
                                            <Th isNumeric>Samples</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {Object.entries(info.cities)
                                            .filter(([city]) => city !== 'unknown')
                                            .sort((a, b) => a[0].localeCompare(b[0]))
                                            .map(([city, cityInfo], idx) => (
                                                <Tr key={city}>
                                                    <Td>{idx + 1}</Td>
                                                    <Td>{city.charAt(0).toUpperCase() + city.slice(1)}</Td>
                                                    <Td isNumeric>{cityInfo.pickupCount}</Td>
                                                    <Td isNumeric>{cityInfo.totalSamples}</Td>
                                                </Tr>
                                            ))}
                                    </Tbody>
                                </Table>
                            </Box>
                        </Box>
                    ))}
            </VStack>
        </Box>
    );
};

export default PickupsStatsByCountyPage;
